import { BehaviorSubject, EMPTY, of, Observable, Subscription } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Input, OnDestroy, OnInit } from '@angular/core';
import { LevelProgressService } from '../../shared/level-progress/level-progress.service';
import { StudyBundleCourseDto } from '../../shared/types/StudyBundleCourseDto';
import { ImmersionCarouselService } from '../immersion-carousel/immersion-carousel.service';
import { CourseContentType } from '../../shared/types/course-content-type';
import { ImmersionSortBy } from '../../shared/types/immersion-sort-by';
import { KeyValueObjectType } from '../../shared/types/key-value-object-type';

/**
 * Parent class for all immersion types (VIDEOS, DAILY_NEWS, IDIOMS) components.
 */
export class ImmersionCarouselComponent implements OnInit, OnDestroy {
  @Input() immersionItems: Array<StudyBundleCourseDto> = [];
  lastPageLoaded = 0;
  immersionType: CourseContentType;
  hasError = false;

  extraParams: Array<KeyValueObjectType>;

  readonly PAGE_SIZE = 10;

  protected includedTopics: string[];
  protected excludedTopics: string[];
  protected filteredStatuses: string[] = ['INCOMPLETE'];
  protected subscription: Subscription = new Subscription();
  protected readonly itemsLoaded$: Observable<boolean>;

  private isLoadingItems = false;
  private readonly itemsLoadedSubject$ = new BehaviorSubject<boolean>(false);

  constructor(
    public service: ImmersionCarouselService,
    public levelProgressService: LevelProgressService,
    immersionType: CourseContentType,
    protected sortBy: ImmersionSortBy = ImmersionSortBy.RECENT
  ) {
    this.immersionType = immersionType;
    this.itemsLoaded$ = this.itemsLoadedSubject$.asObservable();
  }

  ngOnInit() {
    this.subscription.add(
      this.getImmersionItems()
        .pipe(tap(() => this.itemsLoadedSubject$.next(true)))
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  lockLoading() {
    this.isLoadingItems = true;
  }

  unlockLoading() {
    this.isLoadingItems = false;
  }

  getImmersionItems(): Observable<any> {
    return this.service
      .getImmersions(
        [this.immersionType],
        this.lastPageLoaded + 1,
        this.PAGE_SIZE,
        this.includedTopics,
        this.excludedTopics,
        this.sortBy,
        this.filteredStatuses,
        this.extraParams
      )
      .pipe(
        mergeMap((items) => {
          if (items) {
            this.immersionItems = this.immersionItems.concat(items);
            this.lastPageLoaded++;
          }
          return of(items);
        }),
        catchError((error: HttpErrorResponse) => {
          this.hasError = true;
          this.itemsLoadedSubject$.next(true);
          console.error(error.message);
          return EMPTY;
        })
      );
  }

  reloadCourseTileById(courseId: number) {
    const immersionIndex = this.immersionItems.findIndex((immersion) => {
      return immersion.id === courseId;
    });
    if (immersionIndex === -1) {
      return;
    }

    const page = Math.floor(immersionIndex / this.PAGE_SIZE) + 1;
    this.service
      .getImmersions(
        [this.immersionType],
        page,
        this.PAGE_SIZE,
        this.includedTopics,
        this.excludedTopics,
        this.sortBy,
        this.filteredStatuses,
        this.extraParams
      )
      .subscribe(
        (items) => {
          if (items) {
            const isVideoInNewPayload =
              items.findIndex((immersion) => {
                return immersion.id === courseId;
              }) !== -1;
            const firstPart = this.immersionItems.slice(0, (page - 1) * this.PAGE_SIZE);
            const lastPart = isVideoInNewPayload
              ? this.immersionItems.slice(page * this.PAGE_SIZE)
              : this.immersionItems.slice(page * this.PAGE_SIZE + 1);
            this.immersionItems = firstPart.concat(items).concat(lastPart);
          }
        },
        (err: HttpErrorResponse) => {
          console.error(err.message);
        }
      );
    this.levelProgressService.updateLevelProgress();
  }

  trackById(index: number, item: StudyBundleCourseDto): number {
    return item.id;
  }

  onTranslated(event: any) {
    // don't do anything if we don't have any more items to retrieve, event.item.count,
    // the total count of the carousel.tiles
    if (!event || event.item.count % this.PAGE_SIZE !== 0 || this.isLoadingItems) {
      return;
    }

    // load the extra immersion items before the last page is visible to the user.
    // event.page.count - 2 is the last page  + 1, should be event.page.count - 1, because it's zero-based, it uses -2.
    if (event.page && event.page.index >= event.page.count - 2) {
      this.lockLoading();
      this.getImmersionItems().subscribe(() => {
        this.unlockLoading();
      });
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/immersion-carousel/immersion-carousel.component.ts